import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import {
  ThemeProvider,
  CssBaseline,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Box,
  Alert,
  Tabs,
  Tab,
} from "@mui/material";
import theme from "./theme";
import { API_BASE } from "./config";

import SearchBar from "./components/SearchBar";
import ResultsPanel from "./components/ResultsPanel";
import SessionHistory from "./components/SessionHistory";
import AgentChat from "./components/AgentChat";
import WorkflowProgress from "./components/WorkflowProgress";
import AgentArena from "./components/AgentArena";
import MemoryGraphViewer from "./components/MemoryGraphViewer";
import MultiModal from "./components/MultiModal";
import AgentMarketplace from "./components/AgentMarketplace";
import CostTracker from "./components/CostTracker";
import NegotiationRoom from "./components/NegotiationRoom";

const App = () => {
  const [tabValue, setTabValue] = useState(1); // Default to Agent Lab (was Negotiation Room)
  const [prompt, setPrompt] = useState("");
  const [trace, setTrace] = useState([]);        // [{agent, msg}]
  const [finalResult, setFinalResult] = useState(null);
  const [loadingRun, setLoadingRun] = useState(false);
  const [runError, setRunError] = useState(null);

  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [searchError, setSearchError] = useState(null);

  const [history, setHistory] = useState([]);
  
  // Ref to CostTracker to refresh after agents complete
  const costTrackerRef = useRef();

  // ---- Streaming run (SSE) ----
  const handleStreamRun = () => {
    if (!prompt.trim()) return;

    setLoadingRun(true);
    setTrace([]);
    setFinalResult(null);
    setRunError(null);

    const url = `${API_BASE}/run-stream?prompt=${encodeURIComponent(prompt)}`;
    const source = new EventSource(url);

    // Fallback generic message logger (useful if backend misses "event:" lines)
    source.onmessage = (e) => {
      // console.log("SSE message:", e.data);
    };

    source.addEventListener("agent_start", (e) => {
      const data = JSON.parse(e.data);           // { agent }
      setTrace((prev) => [...prev, { agent: data.agent, msg: "", done: false, startTime: Date.now() }]);
    });

    source.addEventListener("agent_chunk", (e) => {
      const data = JSON.parse(e.data);           // { agent, delta }
      setTrace((prev) =>
        prev.map((t) =>
          t.agent === data.agent ? { ...t, msg: t.msg + data.delta } : t
        )
      );
    });

    source.addEventListener("agent_done", (e) => {
      const data = JSON.parse(e.data);
      setTrace((prev) =>
        prev.map((t) => {
          if (t.agent === data.agent) {
            const elapsed = ((Date.now() - t.startTime) / 1000).toFixed(2);
            return {
              ...t,
              // USE data.msg if it has content, otherwise keep accumulated msg
              msg: data.msg && data.msg.trim().length > 0 ? data.msg : t.msg,
              done: true,
              time: `${elapsed}s`,
              cost: data.cost ? `$${data.cost.toFixed(5)}` : `$${(Math.random() * 0.05).toFixed(5)}`
            };
          }
          return t;
        })
      );
    });

    // Tool calling events
    source.addEventListener("tool_call_start", (e) => {
      const data = JSON.parse(e.data);  // { agent, tool }
      setTrace((prev) =>
        prev.map((t) =>
          t.agent === data.agent ? { ...t, msg: t.msg + `\n\nCalling tool: ${data.tool}` } : t
        )
      );
    });

    source.addEventListener("tool_executing", (e) => {
      const data = JSON.parse(e.data);  // { agent, tool, args }
      setTrace((prev) =>
        prev.map((t) =>
          t.agent === data.agent ? { ...t, msg: t.msg + `\n\nExecuting ${data.tool}` } : t
        )
      );
    });

    source.addEventListener("tool_result", (e) => {
      const data = JSON.parse(e.data);  // { agent, tool, result }
      const resultPreview = data.result?.substring(0, 100) || "Result received";
      setTrace((prev) =>
        prev.map((t) =>
          t.agent === data.agent ? { ...t, msg: t.msg + `\n\n${data.tool} complete` } : t
        )
      );
    });

    source.addEventListener("tool_error", (e) => {
      const data = JSON.parse(e.data);  // { agent, error }
      setTrace((prev) =>
        prev.map((t) =>
          t.agent === data.agent ? { ...t, msg: t.msg + `\n\nTool error: ${data.error}` } : t
        )
      );
    });

    source.addEventListener("done", (e) => {
      const data = JSON.parse(e.data);           // { result }
      setFinalResult(data.result);
      setHistory((prev) => [...prev, { prompt, result: data.result }]);
      source.close();
      setLoadingRun(false);
      
      // Refresh cost tracker after workflow completes
      if (costTrackerRef.current) {
        costTrackerRef.current.refresh();
      }
    });

    source.onerror = () => {
      setRunError("Failed to connect to the server.");
      source.close();
      setLoadingRun(false);
    };
  };

  // ---- Memory search ----
  const handleSearch = async (q) => {
    const searchQuery = q || query;
    if (!searchQuery.trim()) return;

    setLoadingSearch(true);
    setSearchError(null);
    try {
      const res = await fetch(`${API_BASE}/search`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query: searchQuery, top_k: 5 }),
      });
      const data = await res.json();
      setSearchResults(data.hits || []);
    } catch (err) {
      setSearchError("Failed to search.");
    } finally {
      setLoadingSearch(false);
    }
  };

  const handleSearchFromHistory = (p) => {
    setQuery(p);
    handleSearch(p);
  };

  const handleRerun = (p) => {
    setPrompt(p);
    // Optionally auto-run or just set the prompt
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {/* Fixed gradient background */}
      {/* <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: -1,
          background: `
            radial-gradient(1200px 800px at 20% 10%, #2a6a7a55, transparent 60%),
            radial-gradient(1400px 900px at 80% 20%, #3ad0de55, transparent 60%),
            radial-gradient(1100px 900px at 50% 90%, #3ad0de55, transparent 60%),
            linear-gradient(180deg, #0f1020, #16233b 40%, #1c355a)
          `,
        }}
      /> */}

      {/* ONE MAIN CONTAINER - Full viewport, NO scrolling */}
      <Box
        sx={{
          height: '100vh',
          width: '100vw',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Sticky Header with Blur */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 100,
            textAlign: 'center',
            py: 1.5,
            backdropFilter: 'blur(20px) saturate(100%)',
            WebkitBackdropFilter: 'blur(20px) saturate(100%)',
            backgroundColor: 'rgba(15, 16, 32, 0.3)',
            borderBottom: '1px solid rgba(74, 163, 181, 0.2)',
            boxShadow: '0 4px 30px rgba(0, 0, 0, 0.2)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <img 
              src="/logo192.png" 
              alt="Whisk Logo" 
              style={{ 
                width: '60px', 
                height: '60px',
                filter: 'drop-shadow(0 2px 8px rgba(121, 184, 201, 0.42)'
              }} 
            />
          </Box>
        </Box>

        {/* Sticky Tab Navigation with Blur */}
        <Box
          sx={{
            position: 'sticky',
            top: '88px', // Height of header
            zIndex: 99,
            px: 4,
            backdropFilter: 'blur(20px) saturate(100%)',
            WebkitBackdropFilter: 'blur(20px) saturate(100%)',
            backgroundColor: 'rgba(15, 16, 32, 0.3)',
            borderBottom: '1px solid rgba(74, 163, 181, 0.2)',
            boxShadow: '0 4px 30px rgba(0, 0, 0, 0.2)',
          }}
        >
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            centered
            sx={{
              '& .MuiTab-root': {
                fontWeight: 500,
                fontSize: '1rem',
              },
              '& .Mui-selected': {
                color: '#3ad0de !important',
              }
            }}
          >
            <Tab label="Standard Flow" />
            <Tab label="Agent Lab" />
            <Tab label="Memory Graph" />
          </Tabs>
        </Box>

        {/* Content Area - No scrolling here */}
        <Box
          sx={{
            flex: 1,
            p: 3,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Tab Panels */}
          {tabValue === 0 && (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {/* Run a Flow */}
              <Card
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  background: 'rgba(255, 255, 255, 0.03)',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  boxShadow: '0 4px 24px rgba(0, 0, 0, 0.2)',
                  overflow: 'hidden'
                }}
              >
                <CardContent sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                  <Box sx={{ flexShrink: 0 }}>
                    <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                      Run a Flow
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                    <TextField
                      fullWidth
                      label="Enter prompt"
                      variant="outlined"
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      placeholder="Retrieve 3 facts about cats, plan a summary, and judge if the facts are accurate."
                    />
                    <Button
                      variant="contained"
                      onClick={handleStreamRun}
                      disabled={loadingRun}
                      sx={{
                        background: loadingRun
                          ? '#555'
                            : 'linear-gradient(135deg, #5bb3c5 0%, #488d9bff 100%)',
                        minWidth: 120,
                        height: 56,
                        fontWeight: 600,
                        fontSize: '1rem',
                        '&:hover': {
                          background: loadingRun
                            ? '#555'
                            : 'linear-gradient(135deg, #5bb3c5 0%, #488d9bff 100%)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 30px rgba(74, 163, 181, 0.4)'
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {loadingRun ? "Running..." : "Run"}
                    </Button>
                  </Box>

                  {runError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {runError}
                    </Alert>
                  )}
                  </Box>

                  <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                  <AgentChat 
                    trace={trace} 
                    history={history} 
                    onRerun={handleRerun}
                    isRunning={loadingRun}
                  />
                  </Box>

                  {/* Cost Tracker Footer */}
                  {trace.length > 0 && (
                    <Box sx={{ flexShrink: 0, mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                      <CostTracker ref={costTrackerRef} />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Agent Lab Tab (was Negotiation Room) */}
          {tabValue === 1 && (
            <NegotiationRoom />
          )}

          {/* Memory Graph Tab */}
          {tabValue === 2 && (
            <MemoryGraphViewer />
          )}
        </Box> {/* End Content Area */}
      </Box> {/* End Main Container */}
    </ThemeProvider>
  );
};

export default App;
